import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import ProfileStore from 'stores/ProfileStore';
import TippingStore from 'stores/TippingStore';
import UserStore from 'stores/UserStore';
import allUserGroups from 'enum/userGroups';
import { MESSAGE_TYPES } from 'enum/messages';

import PerformerMessage from './PerformerMessage';
import AdminMessage from './AdminMessage';
import ModMessage from './ModMessage';
import FanMessage from './FanMessage';
import BasicMessage from './BasicMessage';
import GoldMessage from './GoldMessage';
import CoachMessage from './CoachMessage';
import Message from './Message';
import styles from './ChatMessageTypes.css';


@observer
class MessageComponent extends PureComponent {
  static propTypes = {
    message: PropTypes.object.isRequired,
    openGuestBillingModal: PropTypes.func.isRequired,
    roomName: PropTypes.string.isRequired,
    chatSettings: PropTypes.object,
    embedProfileV2: PropTypes.bool,
    isBroadcasterChat: PropTypes.bool,
    isDesktop: PropTypes.bool,
    openContextualChatModal: PropTypes.func,
    senderFromRoster: PropTypes.object,
  };

  static defaultProps = { senderFromRoster: undefined };

  state = { contextualMenuOpened: false };

  getMessageComponentByMessageType(message, messageSentByPerformer) {
    const {
      isFanclubSubscribed,
      senderAccessLevel,
      senderIsRoomModerator,
    } = message;

    if (messageSentByPerformer) {
      return PerformerMessage;
    } else if (senderAccessLevel === MESSAGE_TYPES.ADMIN) {
      return AdminMessage;
    } else if (senderIsRoomModerator) {
      return ModMessage;
    } else if (senderAccessLevel === MESSAGE_TYPES.COACH) {
      return CoachMessage;
    } else if (
      isFanclubSubscribed &&
      ![MESSAGE_TYPES.GOLD, MESSAGE_TYPES.GOLD_FREE, MESSAGE_TYPES.GOLD_PROMO].includes(senderAccessLevel)
    ) {
      return FanMessage;
    }

    switch (senderAccessLevel) {
      case MESSAGE_TYPES.MODERATOR:
        return AdminMessage;

      case MESSAGE_TYPES.VERIFIED_ROOM_MODERATOR:
        return ModMessage;

      case MESSAGE_TYPES.GOLD:
      case MESSAGE_TYPES.GOLD_FREE:
      case MESSAGE_TYPES.GOLD_PROMO:
        return GoldMessage;

      case MESSAGE_TYPES.BASIC:
      case MESSAGE_TYPES.GENERAL:
      case MESSAGE_TYPES.UNVERIFIED:
      case MESSAGE_TYPES.GUEST:
        return BasicMessage;

      default:
        return BasicMessage;
    }
  }

  setMenuOpened = () => {
    this.setState({ contextualMenuOpened: true });
  };

  setMenuClosed = () => {
    this.setState({ contextualMenuOpened: false });
  };

  render() {
    const {
      chatSettings,
      embedProfileV2,
      isBroadcasterChat,
      isDesktop,
      message,
      openContextualChatModal,
      openGuestBillingModal,
      roomName,
      senderFromRoster,
    } = this.props;

    const { translationSettings: { active, showOriginalLanguage } } = ChatStore;
    const { isLoggedInUserRoomModerator } = FirebaseStore;
    const {
      tippingInfo: { topTipper },
      performerTopTipper,
    } = TippingStore;
    const { user: { username, accessLevel, sfw } } = UserStore;

    const { senderOriginalUsername, isFanclubSubscribed } = message;

    const messageSentByPerformer = roomName === senderOriginalUsername?.toLowerCase?.();
    const translationAvailable = Boolean(message.translatedMessage) && active;
    const senderLowerCased = senderOriginalUsername?.toLowerCase();
    const isTopTipperBroadcasterChat = performerTopTipper?.toLowerCase() === senderLowerCased;
    const isTopTipper = (isBroadcasterChat ? isTopTipperBroadcasterChat : topTipper?.toLowerCase() === senderLowerCased)
    || false;

    const roomModerator = isLoggedInUserRoomModerator(username);
    const hasMarkAsSpamCF = UserStore.hasCF(allUserGroups.USER_REPORTED_MESSAGES);
    const isMessageSentByAdminOrModerator = (
      message.senderAccessLevel === MESSAGE_TYPES.MODERATOR ||
      message.senderAccessLevel === MESSAGE_TYPES.ADMIN
    );

    const StyledComponent = this.getMessageComponentByMessageType(message, messageSentByPerformer);

    const { showRegistrationModal } = ProfileStore;
    const messageClasses = classNames({
      [styles.msgPosition]: true,
      [styles.msgDefault]: !translationAvailable && !showOriginalLanguage,
      [styles.translatedMessage]: translationAvailable && showOriginalLanguage,
      [styles.yellowBorder]: translationAvailable && !isDesktop && showOriginalLanguage,
      [styles.broadcasterChatMessage]: isBroadcasterChat && !isMessageSentByAdminOrModerator,
      [styles.broadcasterChatMessageNoHover]: isBroadcasterChat && isMessageSentByAdminOrModerator,
      [styles.hovered]: isBroadcasterChat && this.state.contextualMenuOpened,
    });

    return (
      <Message
        accessLevel={accessLevel}
        actions={{ showRegistrationModal, openGuestBillingModal }}
        embedProfileV2={embedProfileV2}
        isBroadcasterChat={isBroadcasterChat}
        isDesktop={isDesktop}
        isFanclubSubscribed={isFanclubSubscribed}
        isRoomModerator={roomModerator}
        isTopTipper={isTopTipper}
        isTranslated={translationAvailable}
        message={message}
        setMenuClosed={this.setMenuClosed}
        setMenuOpened={this.setMenuOpened}
      >
        <div className={messageClasses}>
          <StyledComponent
            accessLevel={accessLevel}
            active={active}
            chatSettings={chatSettings}
            embedProfileV2={embedProfileV2}
            hasMarkAsSpamCF={hasMarkAsSpamCF}
            isBroadcasterChat={isBroadcasterChat}
            isDesktop={isDesktop}
            isFanclubSubscribed={isFanclubSubscribed}
            isSfw={sfw}
            isTopTipper={isTopTipper}
            isTranslated={translationAvailable}
            msg={message}
            openContextualChatModal={openContextualChatModal}
            roomModerator={roomModerator}
            roomName={roomName}
            senderFromRoster={senderFromRoster}
            showOriginalLanguage={showOriginalLanguage}
          />
        </div>
      </Message>
    );
  }
}

MessageComponent.defaultProps = {
  chatSettings: {},
  embedProfileV2: false,
  isBroadcasterChat: false,
  isDesktop: false,
  openContextualChatModal: null,
};

export default MessageComponent;
